import { Fragment } from 'react';
import { Link } from 'react-router';
import { IntegrationDiagram } from '@/components/IntegrationDiagram';
import ui from '@/components/ui.module.css';
import { COMPANY } from '@/content/company';
import {
  BEYOND_TAGS,
  DIFFERENTIATORS,
  PLATFORMS,
  PROCESS,
  SERVICES_SUMMARY,
} from '@/content/services';
import styles from './Home.module.css';

export function Home() {
  return (
    <>
      <HeroSection />
      <PlatformsBand />
      <WhatWeDo />
      <BeyondEcommerce />
      <HowWeWork />
      <WhyClients />
      {/*
       * No company-identity band here any more. It restated the legal name,
       * registered office, IČO, DIČ, IČ DPH and incorporation date, and the
       * footer already carries the name, the full address, IČO, IČ DPH and the
       * register entry on this and every other page, with all six on /company.
       * Saying it twice on one page bought nothing and pushed the closing call to
       * action further from the content that earns it.
       *
       * The § 4(1) / § 3a duty is unaffected: it asks for the identification data
       * to be permanently and directly accessible, which the footer satisfies
       * everywhere and /company satisfies in full.
       */}
      <ClosingCta />
    </>
  );
}

/* -------------------------------------------------------------------- hero */

function HeroSection() {
  return (
    <section className={ui.gridBand} aria-labelledby="hero-heading">
      <div className={styles.heroInner}>
        <div>
          <h1 id="hero-heading" className={styles.heroTitle}>
            We design and build practical digital solutions around{' '}
            <span className={styles.heroAccent}>real business needs</span>.
          </h1>
          <p className={styles.heroLead}>
            A small team of engineers working on storefronts, platform applications, integrations
            and the systems behind them. Long-lived code, clear decisions, no theatre.
          </p>
          <div className={styles.heroActions}>
            <Link to="/contact" className={ui.buttonPrimary}>
              Start a conversation
            </Link>
            <Link to="/services" className={ui.buttonSecondary}>
              See what we do
            </Link>
          </div>
        </div>
        <CodePanel />
      </div>
    </section>
  );
}

/**
 * A stylised restatement of the four-step process written out in full further
 * down the page. Decorative, so it stays out of the accessibility tree.
 */
function CodePanel() {
  const lines: { num: string; content: React.ReactNode }[] = [
    {
      num: '01',
      content: (
        <>
          <span className={styles.codeKeyword}>async function</span> workWith(client) {'{'}
        </>
      ),
    },
    {
      num: '02',
      content: (
        <>
          {'  '}
          <span className={styles.codeKeyword}>const</span> goal = understand(client);
        </>
      ),
    },
    {
      num: '03',
      content: (
        <>
          {'  '}
          <span className={styles.codeKeyword}>const</span> plan = scope(goal, honest);
        </>
      ),
    },
    {
      num: '04',
      content: (
        <>
          {'  '}
          <span className={styles.codeKeyword}>const</span> built ={' '}
          <span className={styles.codeKeyword}>await</span> ship(plan);
        </>
      ),
    },
    { num: '05', content: ' ' },
    {
      num: '06',
      content: (
        <>
          {'  '}
          <span className={styles.codeKeyword}>return</span> handOver(built);
        </>
      ),
    },
    { num: '07', content: '}' },
    { num: '08', content: ' ' },
    {
      num: '09',
      content: (
        <span className={styles.codeComment}>
          {'// → { status: '}
          <span className={styles.codeString}>&quot;live&quot;</span>
          {' }'}
        </span>
      ),
    },
  ];

  return (
    <div className={styles.codePanel} aria-hidden="true">
      <div className={styles.codeBar}>
        <span className={styles.codeDot} />
        <span className={styles.codeDot} />
        <span className={styles.codeDot} />
        <span className={styles.codeFile}>how-we-work.ts</span>
      </div>
      <div className={styles.codeBody}>
        {lines.map((line) => (
          <div className={styles.codeLine} key={line.num}>
            <span className={styles.codeNumber}>{line.num}</span>
            <span className={styles.codeText}>{line.content}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

/* --------------------------------------------------------------- platforms */

function PlatformsBand() {
  return (
    <section className={styles.platforms} aria-labelledby="platforms-heading">
      <div className={styles.platformsInner}>
        <h2 id="platforms-heading" className={styles.platformsLabel}>
          Platforms
        </h2>
        <ul className={styles.platformsList}>
          {PLATFORMS.map((platform, index) => (
            <Fragment key={platform}>
              {index > 0 && <span className={styles.platformsDot} aria-hidden="true" />}
              <li>{platform}</li>
            </Fragment>
          ))}
        </ul>
        <p className={styles.platformsNote}>Plus headless &amp; custom builds</p>
      </div>
    </section>
  );
}

/* -------------------------------------------------------------- what we do */

function WhatWeDo() {
  return (
    <section className={styles.section} aria-labelledby="services-heading">
      <h2 id="services-heading" className={ui.sectionTitle}>
        What we do
      </h2>
      <p className={ui.sectionLead}>
        eCommerce first, with the surrounding engineering to make it work.
      </p>
      <div className={styles.cardGrid}>
        {SERVICES_SUMMARY.map((service) => (
          <article className={styles.card} key={service.index}>
            <div className={styles.cardIndex} aria-hidden="true">
              {service.index}
            </div>
            <h3 className={styles.cardTitle}>{service.title}</h3>
            <p className={styles.cardText}>{service.description}</p>
          </article>
        ))}
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------ beyond */

function BeyondEcommerce() {
  return (
    <section className={styles.sectionSpaced} aria-labelledby="beyond-heading">
      <div className={styles.beyond}>
        <div>
          <p className={ui.label}>Beyond eCommerce</p>
          <h2 id="beyond-heading" className={styles.beyondTitle}>
            The same engineering, applied elsewhere.
          </h2>
          <p className={styles.beyondText}>
            Commerce is our specialisation, not our limit. We take on custom software where the
            problem is well defined and the engineering is the hard part - including Roblox game
            development and interactive experiences.
          </p>
          <ul className={ui.chipRow}>
            {BEYOND_TAGS.map((tag) => (
              <li className={ui.chip} key={tag}>
                {tag}
              </li>
            ))}
          </ul>
        </div>
        <IntegrationDiagram caption="Typical integration shape" />
      </div>
    </section>
  );
}

/* ------------------------------------------------------------- how we work */

function HowWeWork() {
  return (
    <section className={styles.sectionSpaced} aria-labelledby="process-heading">
      <div className={styles.ruled}>
        <div className={ui.sectionHead}>
          <h2 id="process-heading" className={ui.sectionTitle}>
            How we work
          </h2>
          <p className={ui.sectionLead}>
            Four steps, whether the engagement is three weeks or three years.
          </p>
        </div>
        <ol className={styles.stepGrid} style={{ listStyle: 'none', margin: 0, padding: 0 }}>
          {PROCESS.map((step, index) => (
            <li
              key={step.index}
              className={[styles.step, index === 0 && styles.stepFirst].filter(Boolean).join(' ')}
            >
              <div className={styles.stepLabel}>{step.index}</div>
              <h3 className={styles.stepTitle}>{step.title}</h3>
              <p className={styles.stepText}>{step.description}</p>
            </li>
          ))}
        </ol>
      </div>
    </section>
  );
}

/* ------------------------------------------------------------ why clients */

function WhyClients() {
  return (
    <section className={styles.sectionSpaced} aria-labelledby="why-heading">
      <div className={styles.ruled}>
        <div className={ui.sectionHead}>
          <h2 id="why-heading" className={ui.sectionTitle}>
            Why clients work with us
          </h2>
          <p className={ui.sectionLead}>What tends to matter once a project is under way.</p>
        </div>
        <ul className={styles.reasonGrid} style={{ listStyle: 'none', margin: 0, padding: 0 }}>
          {DIFFERENTIATORS.map((reason) => (
            <li className={styles.reason} key={reason.title}>
              <span className={styles.reasonBullet} aria-hidden="true" />
              <div>
                <h3 className={styles.reasonTitle}>{reason.title}</h3>
                <p className={styles.reasonText}>{reason.description}</p>
              </div>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}

/* ---------------------------------------------------------------- closing */

function ClosingCta() {
  return (
    <section className={styles.closing} aria-labelledby="home-cta-heading">
      <div className={styles.closingBox}>
        <div>
          <h2 id="home-cta-heading" className={styles.closingTitle}>
            Tell us what you are building.
          </h2>
          <p className={styles.closingText}>
            Send a short description of the project. You will get an honest view of scope, approach
            and fit - not a sales sequence.
          </p>
          <Link to="/contact" className={ui.buttonPrimary}>
            Start a conversation →
          </Link>
        </div>
        <div className={styles.closingAside}>
          <div className={styles.closingAsideTerm}>Direct</div>
          <a href={`mailto:${COMPANY.email}`} className="sk-hit">
            {COMPANY.email}
          </a>
          <div className={styles.closingAsideGap} />
          <div className={styles.closingAsideTerm}>Typical reply</div>
          {COMPANY.replyTime}
        </div>
      </div>
    </section>
  );
}
